import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import FormInput from '../../../shared/components/FormInput';
import DurationFormInput from '../../../shared/components/DurationFormInput';
import IconButton from '../../../shared/components/IconButton';
import TagRelationPicker from '../tags/TagRelationPicker';
import useTags from '../../hooks/useTags';

// The form offers up to 100 and the backend enforces the same bound. Before this, the helper
// text said "max 100" and nothing checked: 500 saved, and the field engineer's task card went
// on to ask for 500 people.
const MIN_PEOPLE = 1;
const MAX_PEOPLE = 100;

export default function NewWorkflowForm({ onCreateWorkflow, workflow = null, onUpdateWorkflow = null, onCancel = null }) {
  const [name, setName] = useState(workflow?.name || '');
  const [description, setDescription] = useState(workflow?.description || '');
  const [assignmentInstructions, setAssignmentInstructions] = useState(workflow?.instruction || '');
  const [selfStartable, setSelfStartable] = useState(workflow?.self_startable || false);
  const [userWorkflow, setUserWorkflow] = useState(workflow?.user_workflow || false);
  const [numberOfPeople, setNumberOfPeople] = useState(workflow?.number_of_people || '');
  const [expectedWorkDuration, setExpectedWorkDuration] = useState(workflow?.expected_work_duration || null);
  const [requiredTools, setRequiredTools] = useState(workflow?.required_tools || '');
  const [requiredParts, setRequiredParts] = useState(workflow?.required_parts || '');
  const [tagIds, setTagIds] = useState(workflow?.tag_ids || []);
  const [nameError, setNameError] = useState('');
  const [descriptionError, setDescriptionError] = useState('');
  const [numberOfPeopleError, setNumberOfPeopleError] = useState('');
  const navigate = useNavigate();
  const { tags } = useTags();

  // A new workflow starts scoped to every asset. Starting it unscoped meant it could be
  // designed, published and then found to be invisible everywhere — the state the admin almost
  // never wants, reached by doing nothing. Picking a tag takes Global off again (see
  // TagRelationPicker), so narrowing stays one gesture.
  //
  // Only on creation: forcing Global onto an existing workflow that happens to have no tags
  // would widen its reach on a save the admin made for some unrelated reason.
  const globalTagId = tags.find((t) => t.is_system)?.id;
  const scopeDefaulted = useRef(Boolean(workflow));

  useEffect(() => {
    if (scopeDefaulted.current || globalTagId === undefined) return;
    scopeDefaulted.current = true;
    setTagIds([globalTagId]);
  }, [globalTagId]);

  const handleSubmit = () => {
    let hasError = false;
    if (!name.trim()) {
      setNameError('Workflow name is required.');
      hasError = true;
    }
    if (!description.trim()) {
      setDescriptionError('Workflow description is required.');
      hasError = true;
    }
    const trimmedPeople = String(numberOfPeople).trim();
    if (trimmedPeople !== '') {
      const parsed = Number(trimmedPeople);
      if (!Number.isInteger(parsed) || parsed < MIN_PEOPLE || parsed > MAX_PEOPLE) {
        setNumberOfPeopleError(
          `Enter a whole number of people between ${MIN_PEOPLE} and ${MAX_PEOPLE}, or leave it empty.`
        );
        hasError = true;
      }
    }
    if (hasError) return;

    const workflowData = {
      name,
      description,
      instruction: assignmentInstructions,
      self_startable: selfStartable,
      user_workflow: userWorkflow,
      number_of_people: trimmedPeople === '' ? null : Number(trimmedPeople),
      expected_work_duration: expectedWorkDuration,
      required_tools: requiredTools,
      required_parts: requiredParts,
      relevant_to: workflow?.relevant_to || null,
      workflow_steps: workflow?.workflow_steps || [],
      tag_ids: tagIds,
    };

    if (workflow && onUpdateWorkflow) {
      onUpdateWorkflow(workflowData);
    } else {
      onCreateWorkflow(workflowData);
    }
  };

  return (
    <div className="d-flex flex-column" style={{padding: 10 }}>
          {/* Workflow Name */}
          <FormInput
            id="workflowName"
            name="workflowName"
            label="Workflow Name"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              if (nameError) setNameError('');
            }}
            placeholder="Enter workflow name"
            helperText="This name will be visible to both field engineers and administrators."
            required
            error={nameError || undefined}
          />

          {/* Description */}
          <FormInput
            id="workflowDescription"
            name="workflowDescription"
            label="Description"
            value={description}
            onChange={(e) => {
              setDescription(e.target.value);
              if (descriptionError) setDescriptionError('');
            }}
            placeholder="Enter workflow description"
            helperText="Internal description visible only to administrators for workflow management purposes."
            rows={3}
            required
            error={descriptionError || undefined}
          />

          {/* Assignment Instructions */}
          <FormInput
            id="assignmentInstructions"
            name="assignmentInstructions"
            label="Assignment Instructions"
            value={assignmentInstructions}
            onChange={(e) => setAssignmentInstructions(e.target.value)}
            placeholder="Enter assignment instructions"
            helperText="These instructions will be included in the email sent to field engineers when they are assigned this workflow."
            rows={2}
          />

          {/* Number of People */}
          <FormInput
            id="numberOfPeople"
            name="numberOfPeople"
            label="Number of People Required"
            type="number"
            value={numberOfPeople}
            onChange={(e) => {
              setNumberOfPeople(e.target.value);
              if (numberOfPeopleError) setNumberOfPeopleError('');
            }}
            placeholder={`Enter number of people (max ${MAX_PEOPLE})`}
            helperText="Maximum number of people required to complete this workflow."
            min={MIN_PEOPLE}
            max={MAX_PEOPLE}
            error={numberOfPeopleError || undefined}
          />

          {/* Expected Work Duration */}
          <DurationFormInput
            id="expectedWorkDuration"
            label="Expected Work Duration"
            value={expectedWorkDuration}
            onChange={setExpectedWorkDuration}
            mode="seconds"
            placeholder="e.g., 2h, 30min, 1d"
            helperText="Expected time to complete this workflow."
          />

          {/* Required Tools */}
          <FormInput
            id="requiredTools"
            name="requiredTools"
            label="Required Tools"
            value={requiredTools}
            onChange={(e) => setRequiredTools(e.target.value)}
            placeholder="List required tools (one per line)"
            helperText="Tools needed to complete this workflow."
            rows={3}
          />

          {/* Required Parts */}
          <FormInput
            id="requiredParts"
            name="requiredParts"
            label="Required Parts"
            value={requiredParts}
            onChange={(e) => setRequiredParts(e.target.value)}
            placeholder="List required parts (one per line)"
            helperText="Parts needed to complete this workflow."
            rows={3}
          />

          {/* Scope (tags) */}
          <div className="mb-4">
            <label className="form-label fw-semibold">Scope</label>
            <TagRelationPicker
              tags={tags}
              selectedIds={tagIds}
              pinGlobal
              onChange={setTagIds}
            />
            {tagIds.length === 0 && (
              <div className="form-text">Not visible to any assets yet.</div>
            )}
          </div>

          {/* Self-startable checkbox */}
          <div className="form-check mb-4">
            <input
              className="form-check-input"
              type="checkbox"
              id="selfStartableCheck"
              checked={selfStartable}
              onChange={(e) => setSelfStartable(e.target.checked)}
            />
            <label className="form-check-label fw-semibold" htmlFor="selfStartableCheck">
              Allow self-starting of workflow
            </label>
            <div className="form-text">
              When enabled, field engineers with access to the asset can start this
              workflow without a prior assignment. Self-startable workflows can still
              be assigned to field engineers from the Assign Task flow and appear in
              the maintenance log.
            </div>
          </div>

          {/* User Workflow checkbox */}
          <div className="form-check mb-4">
            <input
              className="form-check-input"
              type="checkbox"
              id="userWorkflowCheck"
              checked={userWorkflow}
              onChange={(e) => setUserWorkflow(e.target.checked)}
            />
            <label className="form-check-label fw-semibold" htmlFor="userWorkflowCheck">
              User Workflow (anonymous, public)
            </label>
            <div className="form-text">
              For visitor and feedback flows only. Attach this workflow to a public
              short URL so anonymous users can run it without signing in. Tasks appear
              in the asset Feedback tab, not the maintenance log. User workflows cannot
              be assigned to field engineers from Assign Task — use a maintenance or
              self-startable workflow for that instead.
            </div>
          </div>

          <div className="d-grid gap-2">
            <IconButton
              type="button"
              onClick={handleSubmit}
              className="btn btn-primary w-100"
              icon={workflow ? 'bi-check-lg' : 'bi-arrow-right'}
              iconPosition={workflow ? 'start' : 'end'}
            >
              {workflow ? 'Save Changes' : 'Continue to Workflow Design'}
            </IconButton>
            <IconButton
              type="button"
              onClick={() => {
                if (onCancel) {
                  onCancel();
                } else if (workflow) {
                  onUpdateWorkflow(null);
                } else {
                  navigate(-1);
                }
              }}
              className="btn btn-secondary w-100"
            >
              Cancel
            </IconButton>
          </div>
    </div>
  );
}
